// ── BCHNostr — Business verification admin (operator only) ────────
//
// Operator tools to manage paid business verification. Reuses the existing
// grant model (issuer-signed kind 30078) and the existing operator-config
// pattern (fee config honoured only from the operator). Nothing here creates
// or deletes a business identity — revoking only withdraws the badge.

import React, { useMemo, useState } from 'react';
import {
  ShieldCheck, BadgeCheck, Loader2, Check, RefreshCw, X, RotateCcw, Ban, Settings2,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useAllBusinessVerifications, useBusinessVerifyFeeUsd } from '../hooks/useBusinessVerification';
import { isOperatorPubkey } from '../lib/platform';
import {
  buildVerifyGrantEvent, buildVerifyFeeConfigEvent, computeVerificationState,
  formatVerifyDate, DEFAULT_BUSINESS_VERIFY_FEE_USD, type VerifyGrant,
} from '../lib/businessVerify';

const TXID_RE = /^[0-9a-f]{64}$/i;

export default function BusinessVerificationAdmin() {
  const { user } = useAppContext();
  if (!isOperatorPubkey(user?.pubkey)) return null;

  return (
    <div className="space-y-2">
      <VerifyFeePanel />
      <GrantVerificationPanel />
      <VerificationsListPanel />
    </div>
  );
}

// ── Fee configuration ─────────────────────────────────────────────

function VerifyFeePanel() {
  const { publish } = useNostrPublish();
  const { feeUsd } = useBusinessVerifyFeeUsd();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  const current = value === '' ? String(feeUsd) : value;
  const parsed = parseFloat(current);
  const valid = Number.isFinite(parsed) && parsed > 0 && parsed <= 10000;

  const save = async () => {
    if (!valid) return;
    setStatus('saving');
    try {
      const { kind, tags, content } = buildVerifyFeeConfigEvent(parsed);
      await publish({ kind, tags, content, created_at: Math.floor(Date.now() / 1000) });
      setStatus('done');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Settings2 className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold">Business Verification Fee</h3>
        <span className="text-[9px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold">OPERATOR</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        The yearly business-verification price in USD (charged in BCH at the live rate).
        Default ${DEFAULT_BUSINESS_VERIFY_FEE_USD}. Published to all clients instantly.
      </p>
      <div className="flex items-center gap-2">
        <div className="flex items-center bg-muted rounded-lg overflow-hidden flex-1">
          <span className="px-3 text-sm text-muted-foreground font-mono">$</span>
          <input
            type="number"
            min="1"
            step="1"
            value={current}
            onChange={e => setValue(e.target.value)}
            className="flex-1 bg-transparent px-1 py-2 text-sm font-mono focus:outline-none"
          />
          <span className="px-3 text-xs text-muted-foreground">/ year</span>
        </div>
        <button
          onClick={save}
          disabled={!valid || status === 'saving'}
          className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${
            status === 'done' ? 'bg-emerald-500 text-white'
            : status === 'error' ? 'bg-red-500 text-white'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
          }`}
        >
          {status === 'saving' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : status === 'done' ? <><Check className="w-3.5 h-3.5" /> Saved</>
            : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Manual grant ──────────────────────────────────────────────────

function GrantVerificationPanel() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [pubkeyInput, setPubkeyInput] = useState('');
  const [txid, setTxid] = useState('');
  const [status, setStatus] = useState<'idle' | 'publishing' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const resolvedPubkey = useMemo(() => {
    const t = pubkeyInput.trim();
    if (/^[0-9a-f]{64}$/i.test(t)) return t.toLowerCase();
    if (t.startsWith('npub1')) {
      try {
        const d = nip19.decode(t);
        if (d.type === 'npub') return d.data as string;
      } catch { /* invalid */ }
    }
    return '';
  }, [pubkeyInput]);

  const txidValid = !txid.trim() || TXID_RE.test(txid.trim());
  const canSubmit = resolvedPubkey.length === 64 && txidValid && status === 'idle';

  const grant = async () => {
    if (!canSubmit || !user) return;
    setStatus('publishing');
    setErrorMsg('');
    try {
      const { kind, tags, content } = buildVerifyGrantEvent({
        businessPubkey: resolvedPubkey,
        issuedBy: user.pubkey,
        txid: txid.trim() || undefined,
      });
      await publish({ kind, tags, content, created_at: Math.floor(Date.now() / 1000) });
      setStatus('done');
      setTimeout(() => { setStatus('idle'); setPubkeyInput(''); setTxid(''); }, 3000);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to grant');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <BadgeCheck className="w-4 h-4 text-emerald-500" />
        <h3 className="text-sm font-bold">Verify a Business (Manual)</h3>
        <span className="text-[9px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold">OPERATOR</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Grant a Verified BCH Business badge after confirming the payment on-chain. Lasts 1 year.
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Business public key</label>
          <input
            value={pubkeyInput}
            onChange={e => setPubkeyInput(e.target.value)}
            placeholder="npub1… or hex pubkey"
            className="w-full bg-muted rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {pubkeyInput.trim() && !resolvedPubkey && (
            <p className="text-[10px] text-red-500 mt-1">Invalid pubkey — enter a 64-char hex key or npub</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Payment txid (optional, for audit)</label>
          <input
            value={txid}
            onChange={e => setTxid(e.target.value)}
            placeholder="64-char transaction id"
            className="w-full bg-muted rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {!txidValid && <p className="text-[10px] text-red-500 mt-1">Must be a 64-char hex txid</p>}
        </div>
        <button
          onClick={grant}
          disabled={!canSubmit}
          className={`w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${
            status === 'done' ? 'bg-emerald-500 text-white'
            : status === 'error' ? 'bg-red-500 text-white'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
          }`}
        >
          {status === 'publishing' ? <><Loader2 className="w-4 h-4 animate-spin" /> Granting…</>
            : status === 'done' ? <><Check className="w-4 h-4" /> Verified!</>
            : status === 'error' ? errorMsg.slice(0, 40)
            : <><BadgeCheck className="w-4 h-4" /> Grant Verification</>}
        </button>
      </div>
    </div>
  );
}

// ── Existing verifications: view / revoke / restore ───────────────

function VerificationsListPanel() {
  const { grants, loading, refetch } = useAllBusinessVerifications();

  const rows = useMemo(
    () => grants.filter((g): g is VerifyGrant => !!g).sort((a, b) => b.issuedAt - a.issuedAt),
    [grants],
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold">Verified Businesses</h3>
        <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold text-primary">{rows.length}</span>
        <button onClick={() => refetch()} title="Refresh" className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">Scanning relays…</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">No verifications issued yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map(g => <VerificationRow key={g.businessPubkey} grant={g} onChanged={refetch} />)}
        </div>
      )}
    </div>
  );
}

function VerificationRow({ grant, onChanged }: { grant: VerifyGrant; onChanged: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(grant.businessPubkey);
  const { business } = useBusinessProfile(grant.businessPubkey);
  const [busy, setBusy] = useState(false);

  const state = computeVerificationState(grant, null);
  const name = business?.name || profile?.display_name || profile?.name || grant.businessPubkey.slice(0, 10);
  const npub = (() => { try { return nip19.npubEncode(grant.businessPubkey); } catch { return grant.businessPubkey; } })();
  // Heartbeat/marker status: is the discovery marker live on the 30499 profile?
  const markerLive = !!business && business.verifiedUntil > Math.floor(Date.now() / 1000);

  const statusColor =
    state.status === 'VERIFIED' ? 'text-emerald-500 bg-emerald-500/10'
    : state.status === 'EXPIRED' ? 'text-amber-500 bg-amber-500/10'
    : state.status === 'PENDING' ? 'text-blue-500 bg-blue-500/10'
    : 'text-muted-foreground bg-muted';

  const setRevoked = async (revoked: boolean) => {
    if (!user) return;
    setBusy(true);
    try {
      const { kind, tags, content } = buildVerifyGrantEvent({
        businessPubkey: grant.businessPubkey,
        issuedBy: user.pubkey,
        txid: grant.txid || undefined,
        // Restoring reuses the original window if still in the future, else a fresh year.
        startAt: revoked ? grant.startAt : undefined,
        expiresAt: revoked ? grant.expiresAt : undefined,
        revoked,
      });
      await publish({ kind, tags, content, created_at: Math.floor(Date.now() / 1000) });
      setTimeout(onChanged, 1200);
    } catch { /* leave state as-is */ }
    setBusy(false);
  };

  return (
    <div className="rounded-lg border border-border bg-background p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] font-mono text-muted-foreground truncate">{npub}</p>
        </div>
        <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${statusColor}`}>
          {state.status}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
        <div><dt className="text-muted-foreground inline">Verified: </dt><dd className="inline font-medium">{formatVerifyDate(grant.startAt)}</dd></div>
        <div><dt className="text-muted-foreground inline">Expires: </dt><dd className="inline font-medium">{formatVerifyDate(grant.expiresAt)}</dd></div>
        <div className="col-span-2 truncate">
          <dt className="text-muted-foreground inline">Payment txid: </dt>
          <dd className="inline font-mono">{grant.txid || '—'}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground inline">Discoverable on BCH Local: </dt>
          <dd className={`inline font-medium ${markerLive ? 'text-emerald-500' : 'text-muted-foreground'}`}>
            {markerLive ? 'Yes (listing published)' : 'Not yet'}
          </dd>
        </div>
      </dl>

      <div className="flex gap-2">
        {grant.revoked ? (
          <button
            onClick={() => setRevoked(false)}
            disabled={busy}
            className="flex-1 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-semibold flex items-center justify-center gap-1.5 hover:bg-emerald-500/20 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore
          </button>
        ) : (
          <button
            onClick={() => setRevoked(true)}
            disabled={busy}
            className="flex-1 py-1.5 rounded-lg bg-red-500/10 text-red-500 text-[11px] font-semibold flex items-center justify-center gap-1.5 hover:bg-red-500/20 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Revoke
          </button>
        )}
      </div>
      {grant.revoked && (
        <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <X className="w-3 h-3" /> Verification revoked. The business identity and its content are untouched.
        </p>
      )}
    </div>
  );
}
